import {
  Alert, Box, Button, FormControl, FormHelperText, IconButton,
  InputAdornment, InputLabel, OutlinedInput, Paper, Snackbar, TextField, Typography
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { login, register } from "../../utils/AccountService";
import { useNavigate } from "react-router-dom";

export type RegisterProps = {
  onLoginSuccessful: () => void;
}

const Register = (props: RegisterProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [mail, setMail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mailError, setMailError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const onRegisterClicked = useCallback(() => {
    const mailMissing = !mail;
    const usernameMissing = !username;
    const passwordMissing = !password;
    setMailError(mailMissing ? t('mailRequired') : null);
    setUsernameError(usernameMissing ? t('usernameRequired') : null);
    setPasswordError(passwordMissing ? t('passwordRequired') : null);

    if (!mailMissing && !usernameMissing && !passwordMissing) {
      register(mail, username, password).then(response => {
        if (!response.emailNotUnique && !response.usernameNotUnique) {
          login(username, password).then(loginResponse => {
            if (loginResponse.ok) {
              props.onLoginSuccessful();
              navigate('/account');
            }
          });
        } else {
          setSnackbarOpen(true);
          if (response.emailNotUnique) setMailError(t('mailNotUnique'));
          if (response.usernameNotUnique) setUsernameError(t('usernameNotUnique'));
        }
      });
    }
  }, [mail, username, password, props, t, navigate]);

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 4,
    }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 400, borderRadius: 3 }}>
        {/* Brand */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #D97706 0%, #92400E 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 1.5,
            boxShadow: '0 8px 20px rgba(180, 83, 9, 0.3)',
          }}>
            <MenuBookIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            {t('register')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cookbook
          </Typography>
        </Box>

        {/* Fields */}
        <TextField
          fullWidth
          label={t('email')}
          variant="outlined"
          sx={{ mb: 2 }}
          error={!!mailError}
          helperText={mailError}
          value={mail}
          onChange={(e) => setMail(e.target.value)}
          type="email"
          autoComplete="email"
        />

        <TextField
          fullWidth
          label={t('username')}
          variant="outlined"
          sx={{ mb: 2 }}
          error={!!usernameError}
          helperText={usernameError}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
        />

        <FormControl fullWidth variant="outlined" error={!!passwordError}>
          <InputLabel>{t('password')}</InputLabel>
          <OutlinedInput
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(s => !s)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label={t('password')}
          />
          {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
        </FormControl>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          startIcon={<PersonAddIcon />}
          onClick={onRegisterClicked}
          sx={{ mt: 3, py: 1.4 }}
        >
          {t('register')}
        </Button>

        <Button
          fullWidth
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ mt: 1.5 }}
        >
          {t('login')}
        </Button>
      </Paper>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setSnackbarOpen(false)}>
          {t('registerFailure')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Register;
